import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { hotArticles, categories } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"hot" | "new">("hot");

  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = useMemo(() => {
    if (!category) return [];
    const filtered = hotArticles.filter((a) => a.categorySlug === slug);
    if (sortBy === "hot") {
      return [...filtered].sort((a, b) => b.heat - a.heat);
    }
    return [...filtered].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }, [slug, sortBy, category]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">频道未找到</h1>
          <p className="text-foreground-500 mb-6">这个频道可能不存在，去看看其他频道吧</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const displayArticles = categoryArticles.length > 0
    ? categoryArticles
    : hotArticles.filter((a) => a.categorySlug === slug).length > 0
      ? hotArticles.filter((a) => a.categorySlug === slug)
      : [];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${category.name} - 51吃瓜网`,
        "description": `51吃瓜网${category.name}频道，汇集${category.count}篇热门爆料，实时更新最新${category.name}资讯。`,
        "url": `http://csxsjds.cn/category/${slug}`,
        "publisher": {
          "@type": "Organization",
          "name": "51吃瓜网"
        }
      }) }} />

      {/* Category Hero */}
      <section className="relative w-full h-[380px] md:h-[480px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover object-top"
            title={`${category.name} - 51吃瓜网`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40"></div>
        </div>
        <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-accent-500/30 flex items-center justify-center">
              <i className={`${category.icon} text-accent-300`}></i>
            </div>
            {category.isHot && (
              <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold">热门</span>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
            {category.name}专区
          </h1>
          <p className="text-white/60 text-sm md:text-base">
            深挖{category.name}真相，共 {category.count} 条爆料等你来吃
          </p>
        </div>
      </section>

      {/* Content Grid */}
      <section className="py-10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Header Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-900">
                共找到 <span className="text-primary-500">{displayArticles.length}</span> 条瓜料
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                {sortBy === "hot" ? "按热度排序" : "按时间排序"}
              </p>
            </div>

            {/* Sort & Filter */}
            <div className="flex items-center gap-2">
              <div className="flex bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy("hot")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    sortBy === "hot"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  <i className="ri-fire-line mr-1"></i>最热
                </button>
                <button
                  onClick={() => setSortBy("new")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    sortBy === "new"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  <i className="ri-time-line mr-1"></i>最新
                </button>
              </div>
            </div>
          </div>

          {/* Articles Grid */}
          {displayArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayArticles.map((article) => {
                const isNew = new Date(article.publishedAt).getTime() > Date.now() - 24 * 60 * 60 * 1000;
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="bg-background-100 rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                      <div className="relative w-full h-[200px] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          title={article.title}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                        <div className="absolute top-3 right-3 flex gap-1.5">
                          {isNew && (
                            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-foreground-900 text-[10px] font-bold">新</span>
                          )}
                          {article.isHot && (
                            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold">热</span>
                          )}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-400">
                            {new Date(article.publishedAt).toLocaleDateString("zh-CN")}
                          </span>
                          <span className="text-xs text-foreground-400">
                            <i className="ri-eye-line mr-1"></i>
                            {article.heat > 10000 ? `${(article.heat / 10000).toFixed(1)}万` : article.heat}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该频道暂无瓜料</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
